import { SearchOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Row } from 'antd';
import { RoundInput } from 'components/Custom/inputs';
import { TemplateMapping } from 'pages/returns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { turnDrawerOff } from 'store/app/actions';
import styled from 'styled-components';

const LoadEntityWrapper = styled.div.attrs({
  className: 'mapping-form-root',
})`
  font-family: var(--font-family, Inter, sans-serif);
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;

  .entity-search-row {
    flex-shrink: 0;
  }

  .scroller-entity {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    width: 100%;
    padding-right: 2px;
    padding-bottom: 16px;
    margin-bottom: 4px;

    scrollbar-gutter: stable;
  }

  .entity-box {
    width: 100%;
    min-height: 80px;
    border-radius: 10px;
    border: 1px solid #e9f6f7;
    background: var(--color-surface-raised, #ffffff);
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 15px;
    cursor: pointer;
    margin-bottom: 16px;
    box-sizing: border-box;
  }

  .entity-box-selected {
    border: 1px solid var(--color-primary, #41aaaf);
  }

  .entity-name {
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: var(--color-text-dark, #042a2a);
  }

  .entity-other-info {
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    color: var(--color-text-muted, #5a6a6a);
  }

  .entity-box-load {
    margin-top: 4px;
    padding-top: 10px;
    border-top: 1px solid #e3ebeb;
  }

  .entity-box-load-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }
`;
interface ChooseTemplateProps {
  templateMappings: TemplateMapping[];
  loadExistingMapping: (mappingId: string, isTemplate?: boolean) => void;
}

export const ChooseTemplate: React.FC<ChooseTemplateProps> = ({
  templateMappings,
  loadExistingMapping,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedTemplate, setSelectedTemplate] = React.useState<string>();
  const [searchValue, setSearchValue] = React.useState<string>('');
  const [templatesList, setTemplatesList] = React.useState<
    TemplateMapping[] | undefined
  >([]);

  React.useEffect(() => {
    setTemplatesList(templateMappings);
  }, [templateMappings]);

  const onLoadTemplate = (id: string) => {
    loadExistingMapping(id, true);
  };
  const handleLoadClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!selectedTemplate) return;
    onLoadTemplate(selectedTemplate);
    dispatch(turnDrawerOff());
  };

  const handleCancelClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    dispatch(turnDrawerOff());
  };

  const handleChange = (e) => {
    setSearchValue(e.target.value);
    if (e.target.value == '') {
      setTemplatesList(templateMappings);
    }
  };

  const handleSearch = (_e) => {
    if (searchValue != '') {
      setTemplatesList(
        templateMappings?.filter((template) =>
          template?.organisationName
            ?.toLocaleLowerCase()
            .includes(searchValue.toLocaleLowerCase())
        )
      );
    } else {
      setTemplatesList(templateMappings);
    }
  };

  const sortedTemplates = React.useMemo(
    () =>
      [...(templatesList ?? [])].sort((a, b) =>
        a.organisationName.localeCompare(b.organisationName)
      ),
    [templatesList]
  );

  return (
    <LoadEntityWrapper>
      <Row
        justify="center"
        align="middle"
        className="entity-search-row"
        style={{ marginBottom: 16 }}
      >
        <RoundInput
          width="317px"
          height="45px" //@ts-ignore
          bg_color="white"
          size="large"
          style={{ marginBottom: 8 }}
          value={searchValue}
          placeholder={`${t('common.search')} ${t('organisation.entities')}`}
          onChange={handleChange}
          suffix={
            <SearchOutlined
              width={33}
              height={33}
              style={{
                backgroundColor: 'var(--color-primary, #41aaaf)',
                color: 'white',
                padding: '8px',
                borderRadius: '50px',
                fontSize: '16px',
                cursor: 'pointer',
              }}
              onClick={handleSearch}
            />
          }
        />
      </Row>

      <div
        className="scroller-entity"
        tabIndex={0}
        role="region"
        aria-label={t('organisation.entities')}
      >
        {sortedTemplates.map((tm) => {
          if (!tm.organisationName || !tm.country || !tm.mappingId) return null;
          const isSelected = selectedTemplate === tm.mappingId;
          return (
            <div
              key={tm.mappingId}
              className={`entity-box ${
                isSelected ? 'entity-box-selected' : ''
              }`}
              onClick={() => setSelectedTemplate(tm.mappingId)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedTemplate(tm.mappingId);
                }
              }}
            >
              <span className="entity-name">{tm.organisationName}</span>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="entity-other-info">
                  {tm.country} | Created on {tm.createdDate}
                </span>
              </div>
              {isSelected ? (
                <div
                  className="entity-box-load"
                  role="presentation"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <div className="entity-box-load-actions">
                    <ScopeButton
                      type="default" //@ts-ignore
                      width="auto"
                      height="28px"
                      fontSize="12px"
                      lineheight="12px"
                      style={{ paddingLeft: 12, paddingRight: 12 }}
                      onClick={handleCancelClick}
                    >
                      {t('common.cancel')}
                    </ScopeButton>
                    <ScopeButton
                      type="primary" //@ts-ignore
                      width="auto"
                      height="28px"
                      fontSize="12px"
                      lineheight="12px"
                      style={{ paddingLeft: 12, paddingRight: 12 }}
                      onClick={handleLoadClick}
                    >
                      {t('returns.mapping.load-mapping')}
                    </ScopeButton>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </LoadEntityWrapper>
  );
};
